import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert
} from '@mui/material';
import { 
  PointOfSale as POSIcon,
  Computer as SystemIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon
} from '@mui/icons-material';
import AdminButton from 'components/common/AdminButton';

/**
 * Systems tab for employee details - displays external system integrations
 */
function EmployeeSystemsTab({ 
  employee, 
  onUpdate, 
  loading = false 
}) {
  // Use real external systems data from employee.externalSystems
  const externalSystems = employee.externalSystems || [];

  // Map system types to display names and icons
  const systemTypeConfig = {
    'ALOHA_POS': { label: 'Aloha POS', icon: <POSIcon /> },
    'TOAST_POS': { label: 'Toast POS', icon: <POSIcon /> }
  };

  const getSystemConfig = (systemType) => {
    return systemTypeConfig[systemType] || { label: systemType, icon: <SystemIcon /> };
  };


  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  return (
    <Box>
      {/* External Systems List */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          External System Mappings
        </Typography>
        <Divider sx={{ my: 1 }} />
        
        {externalSystems.length === 0 ? (
          <Alert 
            severity="info" 
            sx={{ 
              mt: 2, 
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              overflowWrap: 'break-word'
            }}
          >
            No system mappings configured. This section displays how this employee is identified in external POS and operational systems.
          </Alert>
        ) : (
          <List>
            {externalSystems.map((system, index) => {
              const systemConfig = getSystemConfig(system.systemType);
              
              return (
                <ListItem key={`${system.systemType}-${system.systemId}-${index}`} divider>
                  <ListItemIcon>
                    {systemConfig.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body1" fontWeight="medium">
                          {systemConfig.label}
                        </Typography>
                        <Chip
                          label={system.isActive ? 'Active' : 'Inactive'}
                          color={system.isActive ? 'success' : 'default'}
                          size="small"
                        />
                        {system.isPrimaryPOS && (
                          <Chip
                            label="Primary POS"
                            color="primary"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Employee ID in {systemConfig.label}: {system.systemId}
                        </Typography>
                        {system.systemName && (
                          <Typography variant="body2" color="text.secondary">
                            Employee Name in System: {system.systemName}
                          </Typography>
                        )}
                        {system.syncStatus && (
                          <Typography variant="body2" color="text.secondary">
                            Sync Status: {system.syncStatus}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          Last Sync: {formatDate(system.lastSyncAt)}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </Paper>
    </Box>
  );
}

EmployeeSystemsTab.propTypes = {
  employee: PropTypes.object.isRequired,
  onUpdate: PropTypes.func.isRequired,
  loading: PropTypes.bool
};

export default EmployeeSystemsTab;